import {ChangeEvent, useEffect, useRef, useState, type JSX} from 'react'
import {useParams} from 'react-router'
import {
  useWt,
  useWuShowToast,
  WuButton,
  WuIcon,
  WuLoader,
  WuTextarea,
} from '@npm-questionpro/wick-ui-lib'
import {
  IDialogDetails,
  IResponse,
  IResponseStat,
  IRestrictedUser,
  IStoredDialogUserAvatar,
} from '../../../../types'
import {cx} from '../../../../utils/classNameUtil'
import {dateUtil} from '../../../../utils/dateUtil'
import {GLOBAL} from '../../../../Translation/English/global'
import {DIALOG} from '../../../../Translation/English/dialog'
import {dialogApi} from '../../../../api/dialogApi'
import {BaseModal} from '../../../../components/BaseModal/BaseModal'
import {ConfirmModalContent} from '../../../../components/ConfirmModalContent'
import {DialogAvatar} from '../DialogAvatar/DialogAvatar'
import {DialogReplies} from '../DialogReplies/DialogReplies'
import {DialogTextCard} from '../DialogTextCard/DialogTextCard'
import {ModerationGuidelines} from '../ModerationGuidelines/ModerationGuidelines'
import sharedStyles from '../../dialogShared.module.scss'
import styles from './ResponsesList.module.scss'

interface IResponsesListProps {
  responses: IResponse[]
  dialogDetails: IDialogDetails
  isUserModerator: boolean
}

export const ResponsesList: React.FC<IResponsesListProps> = ({
  responses,
  dialogDetails,
  isUserModerator,
}) => {
  const addDialogResponseReactionMutation =
    dialogApi.useAddDialogResponseReaction()
  const addDialogResponseReplyMutation = dialogApi.useAddDialogResponseReply()
  const deleteDialogResponseMutation = dialogApi.useDeleteDialogResponse()
  const wt = useWt()
  const {showToast} = useWuShowToast()
  const {dialogId} = useParams<{dialogId: string}>()
  const [showModal, setShowModal] = useState<boolean>(false)

  const getDialogUserDetailsByOrg = (): IStoredDialogUserAvatar | null => {
    const data = localStorage.getItem(`dialogUserAvatars`)
    if (!data) return null
    const parsedData = JSON.parse(data)
    if (!Array.isArray(parsedData)) return null
    return (
      parsedData.find(
        (item: IStoredDialogUserAvatar) => item.orgId === dialogDetails.orgId,
      ) ?? null
    )
  }

  const getcurrentUserSessionId = (): string => {
    const sessionIds = JSON.parse(localStorage.getItem(`dialogUserIds`) || '{}')
    const orgId = dialogDetails.orgId ?? ''
    return sessionIds[orgId]
  }
  const currentUserSessionId = getcurrentUserSessionId()
  const currentUserDetails: IStoredDialogUserAvatar | null = !isUserModerator
    ? getDialogUserDetailsByOrg()
    : null

  const [addReplyId, setAddReply] = useState<string | number>('')
  const [expandedReplyIds, setExpandedReplyIds] = useState<
    Set<string | number>
  >(() => new Set())
  const [replyText, setReplyText] = useState<string>('')
  const replyInputRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (addReplyId) {
      replyInputRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      })
    }
  }, [addReplyId])
  const [pendingDeleteResponse, setPendingDeleteResponse] =
    useState<IResponse | null>(null)
  const [deleteModalFooter, setDeleteModalFooter] =
    useState<React.ReactNode>(null)

  const confirmDeleteResponse = async (): Promise<void> => {
    if (!pendingDeleteResponse) return
    try {
      await deleteDialogResponseMutation.mutateAsync({
        id: String(pendingDeleteResponse.id),
        dialogId,
      })
      showToast({message: wt(DIALOG.responseDeleted), variant: 'success'})
      setPendingDeleteResponse(null)
    } catch {
      showToast({
        message: wt(DIALOG.failedToDeleteResponse),
        variant: 'error',
      })
    }
  }

  const userResponseToggle = async (
    response: IResponse,
    isLike: boolean,
  ): Promise<void> => {
    const baseObj = {
      responseId: String(response.id),
      dialogId,
      isLiked: isLike ? !isReacted(response, isLike) : false,
      isDisliked: isLike ? false : !isReacted(response, isLike),
    }
    const obj = isUserModerator
      ? {...baseObj, isModerator: true}
      : {
          ...baseObj,
          dialogUserAvtarId: currentUserDetails?.dialogUserAvatarId,
          dialogAvtarImage: currentUserDetails?.dialogAvatarImageId,
          sessionId: currentUserSessionId,
        }
    try {
      await addDialogResponseReactionMutation.mutateAsync(obj)
    } catch {
      showToast({message: wt(DIALOG.failedToSaveReaction), variant: 'error'})
    }
  }

  const isReacted = (response: IResponse, isLike: boolean): boolean => {
    let reactionStatus = false
    if (isLike) {
      response.reactions.map(reaction => {
        if (reaction.sessionId == currentUserSessionId)
          reactionStatus = reactionStatus || reaction.isLiked
        if (isUserModerator && reaction.isModerator)
          reactionStatus = reactionStatus || reaction.isLiked
      })
    } else {
      response.reactions.map(reaction => {
        reactionStatus =
          reactionStatus ||
          (reaction.isDisliked && reaction.sessionId == currentUserSessionId)
        if (isUserModerator && reaction.isModerator)
          reactionStatus = reactionStatus || reaction.isDisliked
      })
    }

    return reactionStatus
  }

  const addReplyToResponse = async (response: IResponse): Promise<void> => {
    const baseObj = {
      text: replyText,
      responseId: String(response.id),
      isModerator: isUserModerator,
      dialogId,
    }
    const obj = isUserModerator
      ? baseObj
      : {
          ...baseObj,
          dialogUserAvtarId: currentUserDetails?.dialogUserAvatarId,
          dialogAvatarImageId: currentUserDetails?.dialogAvatarImageId,
          sessionId: currentUserSessionId,
        }
    try {
      await addDialogResponseReplyMutation.mutateAsync(obj)
      setReplyText('')
      setAddReply('')
    } catch {
      showToast({message: wt(DIALOG.failedToAddReply), variant: 'error'})
    }
  }

  const toggleReplies = (id: string | number): void => {
    setExpandedReplyIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const expandReplies = (id: string | number): void => {
    setExpandedReplyIds(prev => (prev.has(id) ? prev : new Set(prev).add(id)))
  }

  // A flagged, still-pending reply stays hidden from everyone except the
  // public author who wrote it (who sees the "Reply hidden" warning instead).
  const getVisibleReplies = (response: IResponse): IResponseStat[] =>
    response.replies.filter(
      reply =>
        !reply.isFlagged ||
        reply.status !== 'Pending' ||
        (currentUserSessionId == reply.sessionId && !isUserModerator),
    )

  const getLikes = (response: IResponse): number => {
    return response.reactions?.filter(r => r.isLiked).length
  }

  const getDislikes = (response: IResponse): number => {
    return response.reactions?.filter(r => r.isDisliked).length
  }

  const isModeratedTextByUser = (response: IResponse): boolean => {
    if (
      response.isFlagged &&
      getcurrentUserSessionId() == response.sessionId &&
      !isUserModerator &&
      response.status == 'Pending'
    )
      return true
    return false
  }

  const isUserRestricted = (): boolean => {
    const data = localStorage.getItem(`dialogUserAvatars`)
    const parsedData = data && JSON.parse(data)
    if (
      !Array.isArray(parsedData) ||
      !Array.isArray(dialogDetails.restrictedUsers)
    )
      return false
    const currentUser = parsedData.find(
      (item: IStoredDialogUserAvatar) => item.orgId === dialogDetails.orgId,
    )
    if (!currentUser) return false
    return dialogDetails.restrictedUsers.some(
      (user: IRestrictedUser) => user.username === currentUser.username,
    )
  }

  // Parsed once per render instead of once per comment row — it reads and
  // JSON-parses localStorage, and the answer can't differ between rows.
  const isRestricted = isUserRestricted()

  const moderatedResponseBlock = (response: IResponse): JSX.Element => {
    return (
      <div
        className={styles.responseItem}
        key={`response-card--${response.id}`}
      >
        <div className={sharedStyles.userDetails}>
          <DialogAvatar
            displayName={
              response.isModerator
                ? wt(DIALOG.moderator)
                : (response.dialogUserAvtar?.username ?? '')
            }
            avatarUsername={
              response.isModerator
                ? undefined
                : response.dialogUserAvtar?.username
            }
          />
          <div className={sharedStyles.username}>
            {response.isModerator
              ? wt(DIALOG.moderator)
              : response.dialogUserAvtar?.username}
          </div>
          <div className={sharedStyles.timestamp}>
            {dateUtil.formatDate(response.createdAt, 'mediumWithSeconds')}
          </div>
        </div>
        <div className={styles.moderation}>
          <div className={styles.moderationWarning}>
            <WuIcon
              icon="wm-error-circle-rounded"
              className={styles.moderationIcon}
            />
            {wt(DIALOG.responseWarning)}
          </div>
          <div className={styles.moderationText}>
            {wt(DIALOG.responseWarningDescription)}
            <button
              type="button"
              className={styles.moderationLink}
              onClick={() => setShowModal(true)}
            >
              {wt(DIALOG.communityGuidelines)}
            </button>
          </div>
          <div className={styles.moderatedTimestamp}>
            {dateUtil.formatDate(response.createdAt, 'mediumWithSeconds')}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={isUserModerator ? styles.page : styles.list}>
      {responses.map((response, index) => {
        if (isModeratedTextByUser(response))
          return moderatedResponseBlock(response)
        if (response.isFlagged && response.status != 'Approved') return null
        const visibleReplies = getVisibleReplies(response)
        const isExpanded = expandedReplyIds.has(response.id)
        return (
          <div
            className={
              isUserModerator ? styles.responseItemAdmin : styles.responseItem
            }
            key={`response-card--${response.id}`}
          >
            <DialogTextCard
              data={response}
              isUserModerator={isUserModerator}
              onDeleteRequest={() => setPendingDeleteResponse(response)}
            />
            <div className={styles.replyBlock}>
              <div className={styles.replyActions}>
                {dialogDetails.enableResponse && !isRestricted && (
                  <button
                    type="button"
                    className={styles.addReply}
                    onClick={() => {
                      setAddReply(prev =>
                        prev === response.id ? '' : response.id,
                      )
                      setReplyText('')
                      expandReplies(response.id)
                    }}
                    data-testid={`responses-reply-btn-${index}`}
                  >
                    {wt(DIALOG.reply)}
                    <WuIcon icon="wm-reply" />
                  </button>
                )}
                {visibleReplies.length > 0 && (
                  <button
                    type="button"
                    className={styles.repliesToggle}
                    aria-expanded={isExpanded}
                    onClick={() => toggleReplies(response.id)}
                  >
                    {`${visibleReplies.length} ${
                      visibleReplies.length === 1
                        ? wt(DIALOG.replySingular)
                        : wt(DIALOG.replyPlural)
                    }`}
                    <WuIcon
                      icon={
                        isExpanded
                          ? 'wm-keyboard-arrow-up'
                          : 'wm-keyboard-arrow-down'
                      }
                    />
                  </button>
                )}
              </div>

              {!isRestricted && (
                <div className={styles.replyStats}>
                  {dialogDetails.enableLikes && (
                    <button
                      type="button"
                      className={cx(
                        styles.icon,
                        isReacted(response, true) && styles.reactionActive,
                      )}
                      onClick={() => userResponseToggle(response, true)}
                    >
                      <WuIcon
                        icon={
                          isReacted(response, true)
                            ? 'wm-thumb-up'
                            : 'wm-thumb-up-off'
                        }
                      />
                      {getLikes(response)}
                    </button>
                  )}
                  {dialogDetails.enableDislikes && (
                    <button
                      type="button"
                      className={cx(
                        styles.icon,
                        isReacted(response, false) && styles.reactionActive,
                      )}
                      onClick={() => userResponseToggle(response, false)}
                    >
                      <WuIcon
                        icon={
                          isReacted(response, false)
                            ? 'wm-thumb-down'
                            : 'wm-thumb-down-off'
                        }
                      />
                      {getDislikes(response)}
                    </button>
                  )}
                </div>
              )}
            </div>
            {isExpanded && visibleReplies.length > 0 && (
              <div className={styles.repliesWrapper}>
                <DialogReplies
                  replies={visibleReplies}
                  isUserModerator={isUserModerator}
                />
              </div>
            )}
            {response.id == addReplyId && (
              <div ref={replyInputRef} className={styles.inputBlock}>
                {addDialogResponseReplyMutation.isPending && (
                  <div className={styles.loadingOverlay}>
                    <WuLoader size="sm" />
                  </div>
                )}
                <WuTextarea
                  className={styles.input}
                  placeholder={wt(DIALOG.addReply)}
                  value={replyText}
                  onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
                    setReplyText(e.target.value)
                  }
                  disabled={addDialogResponseReplyMutation.isPending}
                />
                <div className={styles.inputActions}>
                  <button
                    type="button"
                    className={styles.btnCancel}
                    onClick={() => {
                      setAddReply('')
                      setReplyText('')
                    }}
                    disabled={addDialogResponseReplyMutation.isPending}
                  >
                    {wt(GLOBAL.cancel)}
                  </button>
                  <WuButton
                    size="sm"
                    iconOnly
                    variant="secondary"
                    aria-label={wt(DIALOG.submit)}
                    disabled={
                      addDialogResponseReplyMutation.isPending ||
                      replyText.trim().length < 1
                    }
                    onClick={() => addReplyToResponse(response)}
                    Icon={<WuIcon icon="wm-send" />}
                  />
                </div>
              </div>
            )}
          </div>
        )
      })}
      {showModal && (
        <ModerationGuidelines
          showModal={showModal}
          setShowModal={setShowModal}
        />
      )}
      <BaseModal
        visible={!!pendingDeleteResponse}
        headerTitle={`${wt(GLOBAL.delete)} ${wt(DIALOG.response)}`}
        onCloseModal={() => setPendingDeleteResponse(null)}
        isLoading={deleteDialogResponseMutation.isPending}
        maxWidth="600px"
        variant="critical"
        footer={deleteModalFooter}
      >
        <ConfirmModalContent
          isLoading={deleteDialogResponseMutation.isPending}
          description={`${wt(GLOBAL.deleteItemText)} ${wt(DIALOG.response)}?`}
          handleConfirm={confirmDeleteResponse}
          handleCancel={() => setPendingDeleteResponse(null)}
          setContent={setDeleteModalFooter}
          isDestructive
        />
      </BaseModal>
    </div>
  )
}
